import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";
import { Button, Icon, Loader, Pagination, Segment, Table } from "semantic-ui-react";
import { toast } from "../..";
import { IInvoiceCategory } from "../../models/invoice";
import { getInvoiceCategories } from "../../services/invoicesService";
import { AppState } from "../../store/configureStore";
import { AddInvoiceCategoryForm } from "./AddInvoiceCategoryForm";
import { InvoiceCategoryRow } from "./InvoiceCategoryRow";
import "./Invoices.css";

export const InvoiceCategories = () => {
    const user = useSelector((state: AppState) => state.user);
    const allowedRoles = ["admin", "accountant"];
    const loc = useLocation();
    const [loading, setLoading] = useState(false);
    const [invoiceCategories, setInvoiceCategories] = useState<IInvoiceCategory[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(loc.state ? (loc.state as number) : 1);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchInvoiceCategories = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getInvoiceCategories(page);
            if (res.rows) {
                setInvoiceCategories(res.rows as IInvoiceCategory[]);
                setTotalRows(res.totalRows);
            }
        } catch {
            toast("Failed to fetch invoice categories", false);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchInvoiceCategories();
    }, [fetchInvoiceCategories]);

    const handleFetchInvoiceCategories = () => {
        setPage(1);
        fetchInvoiceCategories();
    };

    return !allowedRoles.includes(user.role!) ? (
        <Redirect to="/unauthorized" />
    ) : (
        <div className="invoices-container">
            <div className="container-header">
                <h1>Invoice categories</h1>
                <Button
                    secondary
                    content="Add new invoice category"
                    icon="plus"
                    onClick={() => setModalOpen(true)}
                />
            </div>
            <div className="invoices-display">
                <div className="invoices-table">
                    {loading ? (
                        <Loader active inline="centered" size="huge" />
                    ) : (
                        <Segment.Group>
                            <Segment />
                            <Segment className="table-container">
                                <Table basic="very">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Name</Table.HeaderCell>
                                            <Table.HeaderCell>
                                                No. of invoices in category
                                            </Table.HeaderCell>
                                            <Table.HeaderCell>Actions</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {invoiceCategories.length === 0 ? (
                                            <Table.Row>
                                                <Table.Cell textAlign="center" colSpan="5">
                                                    No matching records found
                                                </Table.Cell>
                                            </Table.Row>
                                        ) : (
                                            invoiceCategories.map(x => (
                                                <InvoiceCategoryRow
                                                    key={x.guid}
                                                    invoiceCategory={x}
                                                    fetchInvoiceCategories={fetchInvoiceCategories}
                                                />
                                            ))
                                        )}
                                        <Table.Row />
                                    </Table.Body>
                                </Table>
                            </Segment>
                            <Segment>
                                {totalRows > 10 && (
                                    <Pagination
                                        size="small"
                                        activePage={page}
                                        onPageChange={(e, data: any) => setPage(+data.activePage)}
                                        ellipsisItem={{
                                            content: <Icon name="ellipsis horizontal" />,
                                        }}
                                        firstItem={{
                                            content: <Icon name="angle double left" />,
                                        }}
                                        lastItem={{
                                            content: <Icon name="angle double right" />,
                                        }}
                                        prevItem={{ content: <Icon name="angle left" /> }}
                                        nextItem={{ content: <Icon name="angle right" /> }}
                                        totalPages={Math.ceil(totalRows / 10)}
                                    />
                                )}
                            </Segment>
                            <AddInvoiceCategoryForm
                                open={modalOpen}
                                closeModal={() => setModalOpen(false)}
                                handleAddInvoiceCategory={handleFetchInvoiceCategories}
                            />
                        </Segment.Group>
                    )}
                </div>
            </div>
        </div>
    );
};
